import {
  Alert,
  Button,
  Grid,
  InputLabel,
  TextField,
  Typography,
} from '@mui/material'
import { isNil } from 'lodash'
import { useLayout } from 'hooks/useLayout'
import { Modal } from 'components/Modal/Modal'
import { object, string, date, number, array } from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { ActivityButton } from 'components/ActivityButton'
import { isPresent } from '@jjvgaming/player-payback-library'
import { ModalFullBody } from 'components/Modal/ModalFullBody'
import { formatISO, isValid, parseISO } from 'date-fns'
import { ModalFullFooter } from 'components/Modal/ModalFullFooter'
import { ModalFullHeader } from 'components/Modal/ModalFullHeader'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import {
  type ContestSubtype,
  type Sweepstake,
  type SweepstakeStatusType,
} from 'types/api'
import { type EditSweepstakeDetailsProps } from './LocationSweepstakeDetailsSection'
import { FormFieldErrorMessage } from 'components/FormFieldErrorMessage/FormFieldErrorMessage'
import { AdornedDateTimeField } from 'components/AdornedDateTimeField/AdornedDateTimeField'
import { SweepstakeStateSelect } from 'components/Shared/SweepstakeStateSelect'
import { ContestSubtypeSelect } from 'components/Shared/ContestSubtypeSelect'
import { LocationSelect } from '../../../../components/Shared/LocationSelect'
import { SelectCorporateAccount } from 'components/SelectCorporateAccount'
import { SelectFromAllOrganizations } from 'components/SelectFromAllOrganizations'
import { useEffect } from 'react'

const isLocationBasedSubtype = (subtype?: string) =>
  subtype === 'Location' || subtype === 'CombinedLocations'

const getLocationSweepstakeType = (sweepstake: Sweepstake): ContestSubtype => {
  if (sweepstake.subtype) {
    return sweepstake.subtype
  }

  if (sweepstake.corporateAccountId) {
    return 'CorporateAccount'
  }

  if (sweepstake.organizationId) {
    return 'Organization'
  }

  return 'Location'
}

export const EditLocationSweepstakeDetailsModal = ({
  sweepstake,
  onSubmitSweepstake,
  toggleModalIsOpen,
  updateIsActive,
  isModalOpen,
}: EditSweepstakeDetailsProps) => {
  const { isMobile } = useLayout()
  const heightModal = isMobile ? '100%' : '85%'
  const NEW_SWEEPSTAKE_DATA_ID = 0
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const SweepstakeDetailsSchema = object().shape({
    Sweepstake: object({
      name: string().required('Must enter a sweepstake name'),
      state: string<SweepstakeStatusType>().required(
        'State is a required field'
      ),
      startDate: date()
        .typeError('Must enter a valid start date')
        .required('Must enter a start date'),
      endDate: date()
        .typeError('Must enter a valid end date')
        .required('Must enter an end date')
        .when(['startDate'], ([startDate], schema) => {
          if (isValid(startDate)) {
            return schema.min(
              startDate,
              'End date should not be before start date'
            )
          }
          return schema
        }),
      locationSweepstakeType: string().required(
        'Must select a sweepstake type'
      ),
      numLocationWinners: number()
        .required('Must enter a number of winners per drawing')
        .transform((val) => (Number.isNaN(val) ? undefined : val))
        .min(1, 'Number of Winners must be greater than or equal to 1')
        .max(99, 'Number of Winners must be less than 100'),
      numAffidavitDays: number()
        .required('Must enter a number of days to complete affidavit')
        .transform((val) => (Number.isNaN(val) ? undefined : val))
        .min(1, 'Number of Winners must be greater than or equal to 1')
        .max(99, 'Number of Winners must be less than 100'),
      marketingSpecialistEmail: string()
        .email('Must enter a valid email')
        .nullable()
        .notRequired()
        .transform((value, originalValue) =>
          originalValue === '' ? null : value
        ),
      licensedEstablishmentIds: array()
        .of(number())
        .transform((value, originalValue) =>
          Array.isArray(originalValue) && originalValue.length === 0
            ? null
            : value
        )
        .when('locationSweepstakeType', {
          is: (type: string) => isLocationBasedSubtype(type),
          then: () =>
            array()
              .of(number())
              .min(1, 'Must select at least one Licensed Establishment')
              .required('Must select at least one Licensed Establishment'),
          otherwise: () => array().of(number()).nullable(),
        }),
      corporateAccountId: number().when('locationSweepstakeType', {
        is: (type: string) => type === 'CorporateAccount',
        then: () => number().required('Must select a Corporate Account'),
        otherwise: () => number().nullable(),
      }),
      organizationId: number().when('locationSweepstakeType', {
        is: (type: string) => type === 'Organization',
        then: () => number().required('Must select an Organization'),
        otherwise: () => number().nullable(),
      }),
      description: string()
        .required('Must enter a sweepstake description')
        .max(500),
    }),
  })

  const formMethods = useForm({
    resolver: yupResolver(SweepstakeDetailsSchema),
    mode: 'all',
    defaultValues: {
      Sweepstake: {
        name: sweepstake.name,
        startDate: getStartDate(sweepstake),
        endDate: getEndDate(sweepstake),
        state: sweepstake.sweepstakeData?.state ?? 'Draft',
        numLocationWinners: sweepstake.numLocationWinners ?? undefined,
        locationSweepstakeType: getLocationSweepstakeType(sweepstake),
        description: sweepstake.description ?? undefined,
        corporateAccountId: sweepstake.corporateAccountId ?? undefined,
        organizationId: sweepstake.organizationId ?? undefined,
        numAffidavitDays: sweepstake.numAffidavitDays ?? undefined,
        marketingSpecialistEmail:
          sweepstake.marketingSpecialistEmail ?? undefined,
      },
    },
  })

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = formMethods

  const locationSweepstakeType = useWatch({
    control,
    name: 'Sweepstake.locationSweepstakeType',
  })

  // Clear dependent fields when locationSweepstakeType changes
  useEffect(() => {
    formMethods.register('Sweepstake.organizationId')
    formMethods.register('Sweepstake.corporateAccountId')
    formMethods.register('Sweepstake.licensedEstablishmentIds')

    if (isLocationBasedSubtype(locationSweepstakeType)) {
      formMethods.setValue('Sweepstake.organizationId', undefined)
      formMethods.setValue('Sweepstake.corporateAccountId', undefined)
    } else if (locationSweepstakeType === 'CorporateAccount') {
      formMethods.setValue('Sweepstake.organizationId', undefined)
      formMethods.setValue('Sweepstake.licensedEstablishmentIds', undefined)
    } else if (locationSweepstakeType === 'Organization') {
      formMethods.setValue('Sweepstake.corporateAccountId', undefined)
      formMethods.setValue('Sweepstake.licensedEstablishmentIds', undefined)
    }
  }, [locationSweepstakeType])

  const handleSubmitWrapper = handleSubmit((data) => {
    onSubmitSweepstake({
      ...sweepstake,
      name: data.Sweepstake.name,
      description: data.Sweepstake.description,
      startDate: formatISO(data.Sweepstake.startDate),
      endDate: formatISO(data.Sweepstake.endDate),
      licensedEstablishmentIds:
        Array.isArray(data.Sweepstake.licensedEstablishmentIds) &&
        data.Sweepstake.licensedEstablishmentIds.length > 0
          ? data.Sweepstake.licensedEstablishmentIds.filter(
              (id: number | undefined): id is number => typeof id === 'number'
            )
          : null,
      corporateAccountId: data.Sweepstake.corporateAccountId,
      organizationId: data.Sweepstake.organizationId,
      numLocationWinners: data.Sweepstake.numLocationWinners,
      numAffidavitDays: data.Sweepstake.numAffidavitDays,
      marketingSpecialistEmail: data.Sweepstake.marketingSpecialistEmail,
      sweepstakeData: {
        contestId: sweepstake.id,
        id: sweepstake.sweepstakeData?.id ?? NEW_SWEEPSTAKE_DATA_ID,
        state: data.Sweepstake.state,
      },
      subtype: data.Sweepstake.locationSweepstakeType as ContestSubtype,
    })
  })

  return (
    <Modal
      isOpen={isModalOpen}
      toggleIsOpen={() => {}}
      sx={{
        width: isMobile ? '100%' : '60%',
        height: heightModal,
      }}
    >
      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmitWrapper} noValidate>
          <ModalFullHeader
            toggleIsOpen={toggleModalIsOpen}
            title={'Edit Location Sweepstake Overview'}
          />
          <ModalFullBody heightModal={heightModal}>
            <Grid container rowSpacing={2}>
              {errors.Sweepstake && (
                <Grid item xs={12} pb={4}>
                  <Alert severity="error">
                    Please correct errors to continue
                  </Alert>
                </Grid>
              )}

              <Grid item container alignItems={'center'} xs={12} md={12}>
                <Grid item xs={12} md={6}>
                  <InputLabel
                    required
                    id="name"
                    sx={{ fontWeight: 'normal', margin: 0 }}
                  >
                    Sweepstake Name
                  </InputLabel>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    {...register('Sweepstake.name', {
                      required: true,
                    })}
                    error={isPresent(errors.Sweepstake?.name)}
                    sx={{ width: '100%' }}
                  />
                  {errors.Sweepstake?.name?.message && (
                    <FormFieldErrorMessage
                      message={errors.Sweepstake.name.message}
                    />
                  )}
                </Grid>
              </Grid>
              <Grid item container alignItems={'center'} xs={12} md={12}>
                <Grid item xs={12} md={6}>
                  <InputLabel
                    required
                    htmlFor="Sweepstake.startDate"
                    sx={{ fontWeight: 'normal', margin: 0 }}
                  >
                    Start Date/Time
                  </InputLabel>
                </Grid>
                <Grid item xs={12} md={6}>
                  <AdornedDateTimeField
                    name="Sweepstake.startDate"
                    control={control}
                    disabled={sweepstake.groupId !== null}
                  />
                  {errors.Sweepstake?.startDate?.message && (
                    <FormFieldErrorMessage
                      message={errors.Sweepstake?.startDate.message}
                    />
                  )}
                </Grid>
              </Grid>

              <Grid item container alignItems={'center'} xs={12} md={12}>
                <Grid item xs={12} md={6}>
                  <InputLabel
                    required
                    htmlFor="Sweepstake.endDate"
                    sx={{ fontWeight: 'normal', margin: 0 }}
                  >
                    End Date/Time
                  </InputLabel>
                </Grid>
                <Grid item xs={12} md={6}>
                  <AdornedDateTimeField
                    name="Sweepstake.endDate"
                    control={control}
                    disabled={sweepstake.groupId !== null}
                  />
                  {errors.Sweepstake?.endDate?.message && (
                    <FormFieldErrorMessage
                      message={errors.Sweepstake?.endDate.message}
                    />
                  )}
                </Grid>
              </Grid>

              <Grid item container alignItems={'center'} xs={12} md={12}>
                <Grid item xs={12} md={6}>
                  <InputLabel
                    required
                    sx={{ fontWeight: 'normal', margin: 0 }}
                    id="Sweepstake.type"
                    disabled={sweepstake.sweepstakeData?.state !== 'Draft'}
                  >
                    Type
                  </InputLabel>
                </Grid>
                <Grid item xs={12} md={6} style={{ maxWidth: '520px' }}>
                  <ContestSubtypeSelect
                    name="Sweepstake.locationSweepstakeType"
                    disabled={sweepstake.sweepstakeData?.state !== 'Draft'}
                    data-testid="SweepstakeTypeSelect"
                    types={[
                      'Location',
                      'CombinedLocations',
                      'CorporateAccount',
                      'Organization',
                    ]}
                  />

                  {errors.Sweepstake?.locationSweepstakeType?.message && (
                    <FormFieldErrorMessage
                      message={
                        errors.Sweepstake?.locationSweepstakeType?.message
                      }
                    />
                  )}
                </Grid>
              </Grid>

              {isLocationBasedSubtype(locationSweepstakeType) && (
                <Grid item container alignItems={'center'} xs={12} md={12}>
                  <Grid item xs={12} md={6}>
                    <InputLabel
                      required
                      sx={{ fontWeight: 'normal', margin: 0 }}
                      id="Sweepstake.licensedEstablishmentIds"
                    >
                      Licensed Establishments
                    </InputLabel>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <LocationSelect
                      showLabel={false}
                      defaultValue={(sweepstake.licensedEstablishments ?? [])
                        .map((x) => x.id)
                        .filter((id): id is number => typeof id === 'number')}
                      defaultSelected={
                        sweepstake.licensedEstablishments ?? undefined
                      }
                      data-testid="LocationSelect"
                      {...register('Sweepstake.licensedEstablishmentIds')}
                    />
                    {isLocationBasedSubtype(locationSweepstakeType) &&
                      errors.Sweepstake?.licensedEstablishmentIds?.message && (
                        <FormFieldErrorMessage
                          message={
                            errors.Sweepstake?.licensedEstablishmentIds?.message
                          }
                        />
                      )}
                  </Grid>
                </Grid>
              )}

              {locationSweepstakeType === 'CorporateAccount' && (
                <Grid item container alignItems={'center'} xs={12} md={12}>
                  <Grid item xs={12} md={6}>
                    <InputLabel
                      required
                      sx={{ fontWeight: 'normal', margin: 0 }}
                      id="Sweepstake.corporateAccountId"
                    >
                      Corporate Account
                    </InputLabel>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <SelectCorporateAccount
                      data-testid="SelectCorporateAccount"
                      error={isPresent(errors.Sweepstake?.corporateAccountId)}
                      {...register('Sweepstake.corporateAccountId')}
                      activityIndicatorMaxWidth="520px"
                      fullWidth
                      corporateAccountIdKey="corporateAccountId"
                      placeholder="Select a Corporate Account"
                    />
                    {locationSweepstakeType === 'CorporateAccount' &&
                      errors.Sweepstake?.corporateAccountId?.message && (
                        <FormFieldErrorMessage
                          message={
                            errors.Sweepstake?.corporateAccountId?.message
                          }
                        />
                      )}
                  </Grid>
                </Grid>
              )}

              {locationSweepstakeType === 'Organization' && (
                <Grid item container alignItems={'center'} xs={12} md={12}>
                  <Grid item xs={12} md={6}>
                    <InputLabel
                      required
                      sx={{ fontWeight: 'normal', margin: 0 }}
                      id="Sweepstake.organizationId"
                    >
                      Organization
                    </InputLabel>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <SelectFromAllOrganizations
                      {...register('Sweepstake.organizationId')}
                      activityIndicatorMaxWidth="520px"
                      error={isPresent(errors.Sweepstake?.organizationId)}
                      fullWidth
                      organizationIdKey="organizationId"
                      placeholder="Select an Organization"
                    />
                    {locationSweepstakeType === 'Organization' &&
                      errors.Sweepstake?.organizationId?.message && (
                        <FormFieldErrorMessage
                          message={errors.Sweepstake?.organizationId?.message}
                        />
                      )}
                  </Grid>
                </Grid>
              )}

              <Grid item container alignItems={'center'} xs={12} md={12}>
                <Grid item xs={12} md={6}>
                  <InputLabel
                    required
                    sx={{ fontWeight: 'normal', margin: 0 }}
                    id="Sweepstake.numLocationWinners"
                  >
                    Number of Winners per Drawing
                  </InputLabel>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    disabled={
                      sweepstake.sweepstakeData?.state === 'WinnerValidation' ||
                      sweepstake.sweepstakeData?.state ===
                        'InternalValidation' ||
                      sweepstake.sweepstakeData?.state === 'Closed' ||
                      sweepstake.sweepstakeData?.state === 'Live'
                    }
                    {...register('Sweepstake.numLocationWinners', {
                      required: true,
                    })}
                    type="number"
                    placeholder={`Enter # of Winners per Drawing`}
                    error={isPresent(errors.Sweepstake?.numLocationWinners)}
                  />
                  {errors.Sweepstake?.numLocationWinners?.message && (
                    <FormFieldErrorMessage
                      message={errors.Sweepstake.numLocationWinners.message}
                    />
                  )}
                </Grid>
              </Grid>
              <Grid item container alignItems={'center'} xs={12} md={12}>
                <Grid item xs={12} md={6}>
                  <InputLabel
                    required
                    sx={{ fontWeight: 'normal', margin: 0 }}
                    id="Sweepstake.numAffidavitDays"
                  >
                    Claim Period
                  </InputLabel>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    {...register('Sweepstake.numAffidavitDays', {
                      required: true,
                    })}
                    type="number"
                    placeholder={`Enter # of Days to Complete Affidavit`}
                    error={isPresent(errors.Sweepstake?.numAffidavitDays)}
                  />
                  {errors.Sweepstake?.numAffidavitDays?.message && (
                    <FormFieldErrorMessage
                      message={errors.Sweepstake.numAffidavitDays.message}
                    />
                  )}
                </Grid>
              </Grid>
              <Grid item container alignItems={'center'} xs={12} md={12}>
                <Grid item xs={12} md={6}>
                  <InputLabel
                    id="marketingSpecialistEmail"
                    sx={{ fontWeight: 'normal', margin: 0 }}
                  >
                    Marketing Specialist Email
                  </InputLabel>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    placeholder="Enter Marketing Specialist Email"
                    {...register('Sweepstake.marketingSpecialistEmail', {})}
                    error={isPresent(
                      errors.Sweepstake?.marketingSpecialistEmail
                    )}
                    sx={{ width: '100%' }}
                  />
                  {errors.Sweepstake?.marketingSpecialistEmail?.message && (
                    <FormFieldErrorMessage
                      message={
                        errors.Sweepstake.marketingSpecialistEmail.message
                      }
                    />
                  )}
                </Grid>
              </Grid>
              <Grid item container alignItems={'center'} xs={12} md={12}>
                <Grid item xs={12} md={6}>
                  <InputLabel
                    required
                    sx={{ fontWeight: 'normal', margin: 0 }}
                    id="Sweepstake.state"
                  >
                    State
                  </InputLabel>
                </Grid>
                <Grid item xs={12} md={6} style={{ maxWidth: '520px' }}>
                  <SweepstakeStateSelect
                    sweepstakeStates={[
                      'Draft',
                      'Live',
                      'InternalValidation',
                      'WinnerValidation',
                      'Closed',
                    ]}
                    disabled={sweepstake.groupId !== null}
                  />

                  {errors.Sweepstake?.state?.message && (
                    <FormFieldErrorMessage
                      message={errors.Sweepstake.state.message}
                    />
                  )}
                </Grid>
              </Grid>

              <Grid
                item
                container
                alignItems={'center'}
                xs={12}
                md={12}
                rowSpacing={3}
              >
                <Grid item xs={12} md={12}>
                  <InputLabel
                    id="Sweepstake.description"
                    required
                    sx={{ fontWeight: 'normal', margin: 0 }}
                  >
                    Sweepstake Description
                  </InputLabel>
                </Grid>
                <Grid item xs={12} md={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={6}
                    required
                    error={isPresent(errors.Sweepstake?.description)}
                    placeholder="Enter description of sweepstake"
                    inputProps={{ maxLength: 500 }}
                    {...register('Sweepstake.description')}
                    sx={{ maxWidth: 'none', width: '100%' }}
                  />
                  <Grid
                    item
                    container
                    direction="row"
                    xs={12}
                    md={12}
                    justifyContent={'flex-end'}
                  >
                    <Typography variant="body-3">500 characters max</Typography>
                  </Grid>
                  {errors.Sweepstake?.description?.message && (
                    <FormFieldErrorMessage
                      message={errors.Sweepstake.description.message}
                    />
                  )}
                </Grid>
              </Grid>
            </Grid>
          </ModalFullBody>
          <ModalFullFooter>
            <Button variant="text" onClick={toggleModalIsOpen}>
              Cancel
            </Button>
            <ActivityButton
              active={updateIsActive}
              type="submit"
              variant="contained"
            >
              Save
            </ActivityButton>
          </ModalFullFooter>
        </form>
      </FormProvider>
    </Modal>
  )
}

const getStartDate = (sweepstake: Sweepstake) => {
  if (isNil(sweepstake.startDate)) {
    return undefined
  }

  return parseISO(sweepstake.startDate)
}

const getEndDate = (sweepstake: Sweepstake) => {
  if (isNil(sweepstake.endDate)) {
    return undefined
  }

  return parseISO(sweepstake.endDate)
}
